import { useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

/**
 * Pick several things out of a list and apply them in one go.
 *
 * Three sections of a class attach things to it - staff, periods and the roll -
 * and before this they shared one control: a <select> and an Add button, one
 * record per round trip. Enrolling a class of twenty five cost seventy five
 * interactions against a menu holding every student in the organization.
 *
 * The standing list stays above this, rendered by the caller. This is only ever
 * the candidates, so a tick is always additive and there is nothing here whose
 * removal needs a date or a confirmation.
 */
export interface ChecklistOption {
  id: string;
  /** Primary text. Matched by the filter. */
  label: string;
  /** Trailing muted text: an email, "left 3 Mar". Also matched. */
  detail?: string;
  /** Struck through and faint: a student who has already left. */
  muted?: boolean;
}

export function Checklist({
  label,
  options,
  picked,
  onPick,
  query,
  onQuery,
  empty,
  noMatch = 'Nothing matches that.',
  searchAfter = 8,
  alwaysSearch = false,
  searchPlaceholder,
  focusRef,
  filters,
  footer,
  disabled,
  children,
}: {
  /** Visible group label, e.g. "Enrol a student". */
  label: string;
  options: ChecklistOption[];
  /** Ids ticked this round. Controlled, because each caller applies differently. */
  picked: ReadonlySet<string>;
  onPick: (id: string, on: boolean) => void;
  /** Controlled, because the periods caller renders Create "<query>" from it. */
  query: string;
  onQuery: (query: string) => void;
  /** Shown in place of the rows when there is nothing to pick. */
  empty: string;
  noMatch?: string;
  /** At or below this many options the search box is not worth its space. */
  searchAfter?: number;
  /** Show it regardless: create-in-place needs somewhere to type a new name. */
  alwaysSearch?: boolean;
  searchPlaceholder?: string;
  /**
   * Put focus back here after applying. It points at the panel, not the search
   * box: the box is not rendered for a short list, and a ref to something that
   * is not there silently does nothing.
   */
  focusRef?: RefObject<HTMLDivElement | null>;
  /** Controls beside the search box, e.g. "include ended". */
  filters?: ReactNode;
  /** A last row that is not an option, e.g. Create "Assembly". */
  footer?: ReactNode;
  disabled?: boolean;
  /** The action row under the list: a date, the apply button, an error. */
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const labelId = useId();
  const listId = useId();
  const statusId = useId();

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.detail?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, query]);

  const searching = alwaysSearch || options.length > searchAfter;

  const summary = searching
    ? `${visible.length} of ${options.length} shown, ${picked.size} selected`
    : `${picked.size} selected`;

  /*
   * The count is shown at once and announced late. Putting one string in both
   * jobs means either a counter that visibly lags your typing, or a screen
   * reader interrupting itself on every keystroke.
   */
  const [announced, setAnnounced] = useState(summary);
  useEffect(() => {
    const timer = window.setTimeout(() => setAnnounced(summary), 300);
    return () => window.clearTimeout(timer);
  }, [summary]);

  /*
   * Arrows are an accelerator laid on top of the normal tab order, not a roving
   * tabindex. Every checkbox stays tabbable, so a group behaves the way a group
   * is expected to; arrows are inert inside one otherwise, so this deviates from
   * no expectation while making a long list quick to walk.
   */
  const roam = (event: React.KeyboardEvent<HTMLElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const boxes = [
      ...event.currentTarget
        .closest('[data-checklist]')!
        .querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not(:disabled)'),
    ];
    if (boxes.length === 0) return;
    event.preventDefault();
    const at = boxes.indexOf(document.activeElement as HTMLInputElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? boxes.length - 1
          : at === -1
            ? 0
            : (at + (event.key === 'ArrowDown' ? 1 : boxes.length - 1) + boxes.length) %
              boxes.length;
    boxes[next]?.focus();
  };

  return (
    /*
     * Focusing the panel bounces to the search box when there is one, and
     * otherwise keeps focus on the panel, which announces as the group. Either
     * way a caller restoring focus after an apply lands somewhere sensible
     * instead of dropping the keyboard at the top of the document, and it does
     * not have to know which case it is in.
     */
    <div
      className="panel p-4"
      data-checklist
      tabIndex={-1}
      ref={focusRef}
      onFocus={(e) => {
        if (e.target === e.currentTarget) input.current?.focus();
      }}
    >
      <p id={labelId} className="eyebrow mb-3">
        {label}
      </p>

      {searching ? (
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="grid w-full gap-1.5 sm:w-auto">
            <span className="eyebrow">Search</span>
            <input
              ref={input}
              type="search"
              className="field w-full sm:w-56"
              value={query}
              placeholder={searchPlaceholder}
              aria-controls={listId}
              aria-describedby={statusId}
              /*
               * Deliberately NOT disabled while a save is in flight.
               *
               * Disabling a focused input blurs it, and callers restore focus
               * here in onSuccess, which fires while the mutation is still
               * pending: the field was disabled at that moment, so focus() did
               * nothing and the keyboard user was dropped on <body>. Typing a
               * filter mid-save is harmless anyway; it is the ticking and the
               * applying that have to hold still.
               */
              onChange={(e) => onQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.preventDefault();
                  onQuery('');
                } else if (e.key === 'ArrowDown') {
                  roam(e);
                }
              }}
            />
          </label>
          {filters}
        </div>
      ) : (
        filters
      )}

      {/*
       * Eyebrow lettering, but ink-soft rather than the eyebrow's own
       * ink-faint. Measured on the sunk panel, ink-faint is 2.54:1 in the
       * light theme against 4.84:1 for ink-soft, and this line is information
       * rather than decoration: it is the only place the number of matches
       * appears.
       */}
      <p id={statusId} className="eyebrow mb-1.5 text-ink-soft">
        <span aria-hidden="true">{summary}</span>
        <span className="sr-only" role="status" aria-live="polite">
          {announced}
        </span>
      </p>

      <div role="group" aria-labelledby={labelId} onKeyDown={roam}>
        <ul className={options.length > 12 ? 'max-h-[19rem] overflow-y-auto' : undefined}>
          {options.length === 0 ? (
            <li className="py-1.5 text-sm text-ink-soft">{empty}</li>
          ) : visible.length === 0 ? (
            <li className="py-1.5 text-sm text-ink-soft">{noMatch}</li>
          ) : (
            visible.map((option) => (
              <li key={option.id} className="border-b border-rule last:border-0">
                <label className="flex items-center gap-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={picked.has(option.id)}
                    disabled={disabled}
                    onChange={(e) => onPick(option.id, e.target.checked)}
                  />
                  <span
                    className={`flex-1 text-sm ${option.muted ? 'text-ink-faint line-through' : ''}`}
                  >
                    {option.label}
                  </span>
                  {option.detail ? (
                    <span className="font-mono text-xs text-ink-soft">{option.detail}</span>
                  ) : null}
                </label>
              </li>
            ))
          )}
          {footer}
        </ul>
      </div>

      {children}
    </div>
  );
}
